'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { coverCategories, coverCategoryTitleColor, coverNumbers, getBackgroundSrc, giftOptionValues } from './honor-card-builder.data.ts';
import { openPrintableCard } from './printable-html-card/printable-html-card.ts';
export const useHonorCardBuilder = () => {
  const $ = _c(36);
  const searchParams = useSearchParams() ?? new URLSearchParams();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["honor-card"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let t1;
  if ($[1] !== t) {
    t1 = giftOptionValues.map(value => ({
      value,
      text: t(`honor-card:giftOptions.${value}`)
    }));
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const giftOptions = t1;
  let t2;
  bb0: {
    const d = searchParams.get("d");
    if (d) {
      try {
        const t3 = decodeURIComponent(d);
        let t4;
        if ($[3] !== t3) {
          t4 = atob(t3);
          $[3] = t3;
          $[4] = t4;
        } else {
          t4 = $[4];
        }
        const decoded = decodeURIComponent(t4);
        t2 = new URLSearchParams(decoded);
        break bb0;
      } catch {
        t2 = null;
        break bb0;
      }
    }
    t2 = null;
  }
  const decodedParams = t2;
  const paramSource = decodedParams ?? searchParams;
  const rawCover = paramSource.get("cover") || "";
  const initialCover = coverCategories.some(c => c.value === rawCover) ? rawCover : coverCategories[0].value;
  const rawNum = Number(paramSource.get("num"));
  let t3;
  if ($[5] !== rawNum) {
    t3 = coverNumbers.includes(rawNum) ? rawNum : coverNumbers[0];
    $[5] = rawNum;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const initialNum = t3;
  const rawGift = paramSource.get("gift") || "";
  const initialGift = giftOptionValues.includes(rawGift) ? rawGift : giftOptionValues[0];
  const initialNote = (paramSource.get("note") || "").slice(0, 500);
  const [coverCategory, setCoverCategory] = useState(initialCover);
  const [coverNumber, setCoverNumber] = useState(initialNum);
  const [giftId, setGiftId] = useState(initialGift);
  const [personalNote, setPersonalNote] = useState(initialNote);
  let t4;
  if ($[7] !== giftId || $[8] !== t) {
    t4 = giftId ? {
      id: giftId,
      label: t(`honor-card:giftOptions.${giftId}`),
      message: t(`honor-card:giftOptions.${giftId}-message`)
    } : undefined;
    $[7] = giftId;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const selectedGift = t4;
  let t5;
  if ($[10] !== coverCategory || $[11] !== coverNumber || $[12] !== giftId || $[13] !== personalNote) {
    t5 = () => {
      const params = new URLSearchParams({
        cover: coverCategory,
        num: String(coverNumber),
        gift: giftId,
        ...(personalNote && {
          note: personalNote
        })
      });
      const encoded = btoa(encodeURIComponent(params.toString()));
      return `/honor-card/interactive/?d=${encodeURIComponent(encoded)}`;
    };
    $[10] = coverCategory;
    $[11] = coverNumber;
    $[12] = giftId;
    $[13] = personalNote;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const buildShareUrl = t5;
  let t6;
  if ($[15] !== coverCategory || $[16] !== coverNumber || $[17] !== personalNote || $[18] !== selectedGift?.label || $[19] !== selectedGift?.message) {
    t6 = async () => {
      trackClick("Honor Card - Print Card");
      await openPrintableCard({
        coverCategory,
        coverNumber,
        personalNote,
        giftLabel: selectedGift?.label ?? "",
        giftMessage: selectedGift?.message ?? "",
        titleColor: coverCategoryTitleColor[coverCategory] ?? "#333",
        getBackgroundSrc
      });
    };
    $[15] = coverCategory;
    $[16] = coverNumber;
    $[17] = personalNote;
    $[18] = selectedGift?.label;
    $[19] = selectedGift?.message;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleDownloadPdf = t6;
  const shareUrl = typeof globalThis.location !== "undefined" ? `${globalThis.location.origin}${buildShareUrl()}` : buildShareUrl();
  let t7;
  if ($[21] !== buildShareUrl) {
    t7 = () => {
      trackClick("Honor Card - View Card");
      globalThis.open(buildShareUrl(), "_blank", "noopener,noreferrer");
    };
    $[21] = buildShareUrl;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleViewCard = t7;
  let t8;
  if ($[23] !== shareUrl) {
    t8 = (emailSubject, emailDescription) => {
      trackClick("Honor Card - Email E-Card");
      const email = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(`${emailDescription} ${shareUrl}`)}`;
      return globalThis.open(email, "_blank", "noopener,noreferrer")?.focus();
    };
    $[23] = shareUrl;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleEmailCard = t8;
  let t9;
  if ($[25] !== coverCategory || $[26] !== coverNumber || $[27] !== giftId || $[28] !== giftOptions || $[29] !== handleDownloadPdf || $[30] !== handleEmailCard || $[31] !== handleViewCard || $[32] !== personalNote || $[33] !== selectedGift || $[34] !== shareUrl) {
    t9 = {
      coverCategory,
      coverNumber,
      giftId,
      selectedGift,
      personalNote,
      setCoverCategory,
      setCoverNumber,
      setGiftId,
      setPersonalNote,
      coverCategories,
      coverNumbers,
      giftOptions,
      handleDownloadPdf,
      handleEmailCard,
      handleViewCard,
      shareUrl
    };
    $[25] = coverCategory;
    $[26] = coverNumber;
    $[27] = giftId;
    $[28] = giftOptions;
    $[29] = handleDownloadPdf;
    $[30] = handleEmailCard;
    $[31] = handleViewCard;
    $[32] = personalNote;
    $[33] = selectedGift;
    $[34] = shareUrl;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
};